import { useMutation } from '@apollo/client/react/hooks';
import InfoHollow from '@clearkit/icons/glyphs/InfoHollow';
import {
  CKBadge,
  CKButton,
  CKCardCollapsible,
  CKTooltip,
} from '@clearkit/react';
import capitalize from 'lodash/capitalize';

import ToastsContainer from '~/containers/Toasts';
import { readableType } from '~/lib/dataEnrichment';

import cancel from './cancel.graphql';
import trigger from './trigger.graphql';

const BackfillTriggerFields = ({
  integration,
  objectType,
  accountConnector,
  isActive,
  setTriggering,
  hasEnrichmentBetaUIFlag,
  hasSavedSelectiveEnrichmentConfig,
  accountConnectorBackfillActive,
  setAccountConnectorBackfillActive,
}) => {
  const [triggerBackfill, { loading: triggering }] = useMutation(trigger, {
    onCompleted: () => {
      setTriggering(false);
      setAccountConnectorBackfillActive(true);
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} backfill started!`,
      );
    },
    onError: () => {
      setTriggering(false);
      ToastsContainer.addError(`There was an error starting this backfill.`);
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });

  const [cancelBackfill, { loading: cancelling }] = useMutation(cancel, {
    onCompleted: () => {
      setAccountConnectorBackfillActive(false);
      ToastsContainer.addSuccess(
        `${capitalize(integration)} ${capitalize(
          objectType.name,
        )} backfill cancelled!`,
      );
    },
    onError: () => {
      ToastsContainer.addError(`There was an error cancelling this backfill.`);
    },
    refetchQueries: ['FieldMappingForm'],
    variables: {
      input: {
        id: accountConnector?.id,
        targetObjectType: objectType.name,
      },
    },
  });
  const startBackfill = () => {
    setTriggering(true);
    triggerBackfill();
  };

  const backfillLoading = triggering || cancelling;
  return hasEnrichmentBetaUIFlag ? (
    <SelectiveEnrichmentBackfillConfig
      integration={integration}
      objectType={objectType}
      accountConnectorBackfillActive={accountConnectorBackfillActive}
      backfillLoading={backfillLoading}
      cancelBackfill={cancelBackfill}
      startBackfill={startBackfill}
      isActive={isActive}
      hasSavedSelectiveEnrichmentConfig={hasSavedSelectiveEnrichmentConfig}
    />
  ) : (
    <BaseBackfillConfig
      integration={integration}
      objectType={objectType}
      accountConnectorBackfillActive={accountConnectorBackfillActive}
      backfillLoading={backfillLoading}
      cancelBackfill={cancelBackfill}
      startBackfill={startBackfill}
      isActive={isActive}
    />
  );
};

function SelectiveEnrichmentBackfillConfig({
  integration,
  objectType,
  accountConnectorBackfillActive,
  startBackfill,
  backfillLoading,
  cancelBackfill,
  isActive,
  hasSavedSelectiveEnrichmentConfig,
}) {
  return (
    <div className="px-4 mt-10 mb-6">
      <CKCardCollapsible
        header={
          <SelectiveEnrichmentBackfillHeader
            integration={integration}
            objectType={objectType}
            accountConnectorBackfillActive={accountConnectorBackfillActive}
          />
        }
        footerClasses="bg-gray-0"
        footer={
          hasSavedSelectiveEnrichmentConfig ? (
            <SelectiveEnrichmentBackfillFooter
              integration={integration}
              objectType={objectType}
            />
          ) : null
        }>
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col mr-20 w-96">
            <p className="text-base font-medium text-gray-800">
              Start a backfill enrichment on {readableType(integration)}{' '}
              {objectType.name}s
            </p>
          </div>
          {accountConnectorBackfillActive ? (
            <CKButton
              isLoading={backfillLoading}
              variant={CKButton.Variant.simple}
              variantColor={CKButton.VariantColor.red}
              onClick={cancelBackfill}>
              Cancel Backfill
            </CKButton>
          ) : (
            <StartBackfillButton
              disabled={!isActive}
              onClick={startBackfill}
              isLoading={backfillLoading}
            />
          )}
        </div>
      </CKCardCollapsible>
    </div>
  );
}

function SelectiveEnrichmentBackfillFooter({ integration, objectType }) {
  return (
    <div className="flex flex-row items-center ">
      <div className="flex flex-col mr-4">
        <InfoHollow />
      </div>
      Selective sync is on. Enriched {objectType.name} data will only be synced
      to {readableType(integration)} for {objectType.name}s that meet your
      selective sync criteria.
    </div>
  );
}

function SelectiveEnrichmentBackfillHeader({
  integration,
  objectType,
  accountConnectorBackfillActive,
}) {
  return (
    <>
      <div
        className="grid grid-cols-[1fr,auto]"
        data-testid="backfillConfigCardHeader">
        <div className="text-base">
          <h3 className="mb-2 font-medium">Backfill enrichment</h3>
          <p>
            Use this to enrich existing {objectType.name}s found within{' '}
            {readableType(integration)}. This can be helpful if you’ve added
            field mappings and want these enriched fields synced to{' '}
            {objectType.name}s already in your CRM. This process may take some
            time.
          </p>
        </div>
        {accountConnectorBackfillActive ? (
          <CKBadge
            className="self-start text-xs text-green-700 bg-green-100"
            variant="default">
            Backfill running
          </CKBadge>
        ) : null}
      </div>
    </>
  );
}
function BaseBackfillConfig({
  integration,
  objectType,
  accountConnectorBackfillActive,
  startBackfill,
  backfillLoading,
  cancelBackfill,
  isActive,
}) {
  return (
    <div className="flex flex-row items-center justify-between pt-4">
      <div className="flex flex-col mr-20 w-96">
        <p className="mb-2 text-base font-medium text-gray-800">
          Backfill {readableType(integration)} {objectType.name} fields
        </p>
        <p className="max-w-3xl text-sm text-gray-600">
          Enrich all {objectType.name}s in {readableType(integration)}. This
          process may take some time.
        </p>
      </div>
      {accountConnectorBackfillActive ? (
        <CKButton
          isLoading={backfillLoading}
          variant={CKButton.Variant.simple}
          variantColor={CKButton.VariantColor.red}
          onClick={cancelBackfill}>
          Cancel Backfill
        </CKButton>
      ) : (
        <StartBackfillButton
          disabled={!isActive}
          onClick={startBackfill}
          isLoading={backfillLoading}
        />
      )}
    </div>
  );
}

function StartBackfillButton({ disabled, isLoading, onClick }) {
  if (disabled) {
    return (
      <CKTooltip
        tooltip={<p className="m-2">Enrichment needs to be enabled</p>}
        placement="top">
        <div>
          <CKButton disabled isLoading={isLoading}>
            Start Backfill
          </CKButton>
        </div>
      </CKTooltip>
    );
  }

  return (
    <CKButton isLoading={isLoading} onClick={onClick}>
      Start Backfill
    </CKButton>
  );
}
export default BackfillTriggerFields;
